import { Box, Checkbox, MenuItem, Typography, useTheme } from "@mui/material";
import { Column } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useLocalization } from "../../hooks/useLocalization";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { FacetOption } from "./types";

interface FilterModalProps<T extends object> {
  isOpen: boolean;
  onClose: () => void;
  columns: Column<T>[];
  facetedColumnIds: string[];
}

export const FilterModal = <T extends object>({
  isOpen,
  onClose,
  columns,
  facetedColumnIds,
}: FilterModalProps<T>): React.ReactElement => {
  const [selectedColumnId, setSelectedColumnId] = useState<string>("");
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);

  const { t } = useLocalization();
  const theme = useTheme();

  const facetedColumns = useMemo(
    () => columns.filter((column) => facetedColumnIds.includes(column.id)),
    [columns, facetedColumnIds],
  );

  const selectedColumn = facetedColumns.find(
    (column) => column.id === selectedColumnId,
  );

  const facetedUniqueValues = selectedColumn?.getFacetedUniqueValues();
  const options: FacetOption[] = facetedUniqueValues
    ? Array.from(facetedUniqueValues.entries())
        .sort((a, b) => {
          const [valueA] = a as [unknown, number];
          const [valueB] = b as [unknown, number];

          return String(valueA).localeCompare(String(valueB));
        })
        .map((entry) => {
          const [value, count] = entry as [unknown, number];

          return {
            label: String(value),
            value: String(value),
            count: Number(count),
          };
        })
    : [];

  useEffect(() => {
    if (isOpen && facetedColumns.length > 0) {
      const firstColumnId = facetedColumns[0]?.id ?? "";
      setSelectedColumnId(firstColumnId);

      const firstColumn = facetedColumns[0];
      const currentFilterValue = firstColumn?.getFilterValue();
      const currentSelectedValues = Array.isArray(currentFilterValue)
        ? (currentFilterValue as string[])
        : [];
      setTempSelectedValues(currentSelectedValues);
    }
  }, [isOpen, facetedColumns]);

  const handleToggleOption = (optionValue: string) => {
    setTempSelectedValues((prev) =>
      prev.includes(optionValue)
        ? prev.filter((v) => v !== optionValue)
        : [...prev, optionValue],
    );
  };

  const handleToggleAll = () => {
    const allValues = options.map((option) => option.value);
    const isAllValuesSelected = allValues.every((value) =>
      tempSelectedValues.includes(value),
    );

    if (isAllValuesSelected) {
      setTempSelectedValues([]);
    } else {
      setTempSelectedValues(allValues);
    }
  };

  const handleApplyFilters = () => {
    if (selectedColumn) {
      selectedColumn.setFilterValue(
        tempSelectedValues.length > 0 ? tempSelectedValues : undefined,
      );
    }
    onClose();
  };

  const handleCancelFilters = () => {
    onClose();
    setTempSelectedValues([]);
  };

  const isAllSelected =
    options.length > 0 &&
    options.every((option) => tempSelectedValues.includes(option.value));

  return (
    <ConfirmationDialog.Root
      fullWidth
      maxWidth="xs"
      onClose={handleCancelFilters}
      open={isOpen}
      sx={{
        "& .MuiDialog-paper": { padding: "16px", borderRadius: "24px" },
      }}
    >
      <ConfirmationDialog.Title showIcon={false} sx={{ padding: "0px" }}>
        {t("components.dataTable.filter")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content sx={{ paddingX: "0px", mt: 2 }}>
        <Box>
          {options.length > 0 && (
            <Box>
              <FilterOption
                handleToggleOption={handleToggleAll}
                isSelected={isAllSelected}
                label={t("components.dataTable.all")}
                showBorder
                value="all"
              />
              {options.map((option) => {
                const isSelected = tempSelectedValues.includes(option.value);
                const onClick = () => handleToggleOption(option.value);

                return (
                  <FilterOption
                    handleToggleOption={onClick}
                    isSelected={isSelected}
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions sx={{ padding: "0px" }}>
        <ConfirmationDialog.Button
          onClick={handleCancelFilters}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("components.dataTable.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          onClick={handleApplyFilters}
          variant="contained"
        >
          {t("components.dataTable.apply")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};

interface FilterOptionProps {
  value: string;
  label: string;
  isSelected: boolean;
  showBorder?: boolean;
  handleToggleOption: (optionValue: string) => void;
}

const FilterOption = ({
  value,
  label,
  isSelected,
  handleToggleOption,
  showBorder,
}: FilterOptionProps) => {
  const theme = useTheme();

  return (
    <MenuItem
      key={value}
      onClick={() => handleToggleOption(value)}
      sx={{
        padding: 0,
        height: 20,
        marginBottom: 1,
        minHeight: "auto",
        ...(showBorder
          ? {
              borderBottom: 1,
              borderColor: "divider",
              paddingBottom: "8px",
              height: 28,
            }
          : {}),
      }}
    >
      <Checkbox
        checked={isSelected}
        sx={{
          width: 20,
          "& .MuiSvgIcon-root": {
            fontSize: 24,
            color: theme.colors.primary[5],
          },
        }}
      />
      <Typography fontSize={12} fontWeight={500} ml={2}>
        {label}
      </Typography>
    </MenuItem>
  );
};
